import {
  alpha,
  Button,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  MuiThemeProvider,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@material-ui/core';
import React from 'react';
import { lightThemeV2 } from '../../settings/themes/lightThemeV2.js';
import { HeaderContent } from '../../components/molecules/HeaderContent.jsx';
import { BodyContent } from '../../components/molecules/BodyContent.jsx';
import { WideContainer } from '../../components/atoms/WideContainer.jsx';
import { HeadCell } from '../../components/molecules/Table/HeadCell.jsx';
import { palette } from '../../settings/themes/colors.js';
import { BasicTableCell } from '../../components/molecules/Table/BasicTableCell.jsx';
import { useGetInspections } from '../../queries/queryInspection.js';
import PropTypes from 'prop-types';
import { getCurrentUser } from '../../utils/auth.js';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { parseStringDateToFormat } from '../../utils/utils.js';
import { FormattedMessage, useIntl } from 'react-intl';
import { InspectionsFilters, RESET_FILTER_VALUES } from '../../components/organims/InspectionsFilters.jsx';
import { ExportInspectionsModal } from '../../components/organims/ExportInspectionsModal/index.jsx';
import { STATUS_CHIP_COLORS } from './inspectionStatus.js';
import classNames from 'classnames';

const useStyles = makeStyles({
  root: {
    height: '100vh',
    '& tr ': {
      borderBottom: `1px solid ${palette.darkGrayTable}`
    }
  },
  dataCell: {
    backgroundColor: palette.white
  },
  chip: {
    borderRadius: 8,
    border: `1px solid ${alpha(palette.dimGray, 0.2)}`,
    color: `${palette.white} !important`,
    backgroundColor: palette.captuDataYellow,
    fontWeight: 'bold'
  },
  link: {
    color: palette.captudataBlue,
    textDecoration: 'none',
    cursor: 'pointer'
  },
  containerFilter: {
    gap: '10px',
    display: 'flex',
    padding: '0px 20px 10px 20px',
    alignItems: 'center'
  },
  clickDesabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: 0.5
  },
  loader: {
    padding: 24,
    textAlign: 'center'
  }
});

const TYPE_CHIP_COLORS = {
  'project.inspections.type.byRoute': palette.captudataBlue,
  'project.inspections.type.byForm': palette.captudataBlue50
};

export const INSPECTION_TYPES = Object.freeze({
  ROUTE: 'route',
  FORMULARY: 'formulary'
});

const getTranslatedKeys = (inspection, intlMessages) => {
  const isFormulary = inspection.inspectionType === INSPECTION_TYPES.FORMULARY;
  const typeKey = isFormulary ? 'project.inspections.type.byForm' : 'project.inspections.type.byRoute';
  const translationTypeKey = intlMessages[typeKey];
  const statusKey = `project.inspection.status.${inspection.status}`;
  const translationStatusKey = intlMessages[statusKey];

  return { typeKey, translationTypeKey, statusKey, translationStatusKey };
};

export const InspectionsComponent = ({ clientId }) => {
  const classes = useStyles();
  const { messages: intlMessages } = useIntl();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [filters, setFilters] = React.useState(RESET_FILTER_VALUES);
  const [isExportModalOpen, setExportModalOpen] = React.useState(false);

  const areFiltersApplied = React.useMemo(() => Object.keys(filters).some(key => filters[key] !== RESET_FILTER_VALUES[key]), [filters]);

  const { data: { inspections = [], meta = {} } = {}, isLoading, isFetching } = useGetInspections({
    clientId,
    params: {
      page: page + 1,
      pageSize,
      ...filters,
      query: filters.query
    }
  });

  const handleClearFilters = () => {
    setFilters(RESET_FILTER_VALUES);
    setPage(0);
  };

  const getChipStatusColor = statusKey => STATUS_CHIP_COLORS[statusKey] || palette.captuDataYellow;
  const getChipTypeColor = typeKey => TYPE_CHIP_COLORS[typeKey] || palette.captudataBlue;
  const onChangePage = (_, newPage) => setPage(newPage);

  const onChangeRowsPerPage = event => {
    const newPageSize = parseInt(event.target.value, 10);

    setPageSize(newPageSize);
    setPage(0);
  };

  const labelDisplayedRows = ({ from, to, count }) =>
    `${from}-${to} ${intlMessages['table.of']} ${count !== -1 ? count : `${intlMessages['table.moreThan']} ${to}`}`;

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(0);
  };

  return (
    <MuiThemeProvider theme={lightThemeV2}>
      <HeaderContent title={intlMessages['project.inspection.inspections']}>
        <Button variant="contained" onClick={() => setExportModalOpen(true)}>
          <FormattedMessage id="inspections.export.button" defaultMessage="Descargar" />
        </Button>
      </HeaderContent>
      <InspectionsFilters filters={filters} onFiltersChange={handleFiltersChange} onClearFilters={handleClearFilters} />
      <BodyContent component={WideContainer}>
        <div className={classes.root}>
          {isLoading || isFetching ? (
            <Grid item xs={12} className={classes.loader}>
              <CircularProgress />
            </Grid>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeadCell>
                      <FormattedMessage id="common.number" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="common.date" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="group.project" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="group.type" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="project.inspections.type.name.form" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="project.inspections.inspector" />
                    </HeadCell>
                    <HeadCell>
                      <FormattedMessage id="common.status" />
                    </HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(inspections || []).length === 0 && !isLoading && !isFetching && !areFiltersApplied && (
                    <TableRow>
                      <BasicTableCell colSpan={7} align="center" style={{ background: palette.white }}>
                        <FormattedMessage id="project.inspections.empty" />
                      </BasicTableCell>
                    </TableRow>
                  )}
                  {(inspections || []).length === 0 && !isLoading && !isFetching && areFiltersApplied && (
                    <TableRow>
                      <BasicTableCell colSpan={7} align="center" style={{ background: palette.white }}>
                        <FormattedMessage id="project.inspections.noInspectionsFound" />
                      </BasicTableCell>
                    </TableRow>
                  )}
                  {inspections.map(inspection => {
                    const { typeKey, translationTypeKey, statusKey, translationStatusKey } = getTranslatedKeys(inspection, intlMessages);

                    return (
                      <TableRow key={inspection.id} className={classes.dataCell}>
                        <BasicTableCell>
                          <Link
                            className={classes.link}
                            to={
                              inspection.inspectionType === INSPECTION_TYPES.FORMULARY
                                ? `/operative/projects/${inspection.project.id}/inspectionspunctual/${inspection.id}`
                                : `/operative/projects/${inspection.project.id}/inspectionslineal/${inspection.id}`
                            }
                          >
                            <Typography>{`#${inspection.id}`}</Typography>
                          </Link>
                        </BasicTableCell>
                        <BasicTableCell>
                          <Typography style={{ minWidth: '86px' }}>{parseStringDateToFormat(inspection.inspectedAt)}</Typography>
                        </BasicTableCell>
                        <BasicTableCell>
                          <Typography>{(inspection.project || {}).name || '-'}</Typography>
                        </BasicTableCell>
                        <BasicTableCell>
                          <Chip
                            label={translationTypeKey}
                            className={classes.chip}
                            style={{ backgroundColor: getChipTypeColor(typeKey) }}
                          />
                        </BasicTableCell>
                        <BasicTableCell>
                          <Typography>{inspection.formularyName}</Typography>
                        </BasicTableCell>
                        <BasicTableCell>
                          <Typography style={{ minWidth: '200px' }}>{inspection.inspector.fullName}</Typography>
                        </BasicTableCell>
                        <BasicTableCell>
                          <Chip
                            label={translationStatusKey}
                            className={classes.chip}
                            style={{ backgroundColor: getChipStatusColor(statusKey) }}
                          />
                        </BasicTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className={classes.dataCell}>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  className={classNames(classes.dataCell, isLoading && classes.clickDesabled)}
                  component="div"
                  rowsPerPage={pageSize}
                  count={meta.totalCount || 0}
                  onPageChange={onChangePage}
                  labelRowsPerPage={intlMessages['table.rowsPerPage.inspector']}
                  labelDisplayedRows={labelDisplayedRows}
                  onRowsPerPageChange={onChangeRowsPerPage}
                  page={page}
                />
              </div>
            </>
          )}
        </div>
      </BodyContent>
      <ExportInspectionsModal
        open={isExportModalOpen}
        onClose={() => setExportModalOpen(false)}
        clientId={clientId}
        totalCount={meta.totalCount}
        projectIds={filters.projectIds ? [filters.projectIds] : undefined}
        formularyIds={filters.formularyIds ? [filters.formularyIds] : []}
        inspectedAtRange={filters.startDate && filters.endDate ? { startDate: filters.startDate, endDate: filters.endDate } : undefined}
        published={filters.published}
        type={filters.type || undefined}
        query={filters.query || undefined}
      />
    </MuiThemeProvider>
  );
};

InspectionsComponent.propTypes = {
  clientId: PropTypes.number
};
const mapToProps = state => ({ clientId: getCurrentUser(state).client_id });

export const Inspections = connect(mapToProps)(withRouter(InspectionsComponent));
